import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { usePermission } from "../../../hooks/usePermission";
import { warning } from "../../../util/Alert";
import { baseURL } from "../../../util/config";
import Button from "../../extra/Button";
import Iconb from "../../extra/Iconb";
import Pagination from "../../extra/Pagination";
import Searching from "../../extra/Searching";
import Table from "../../extra/Table";
import ToggleSwitch from "../../extra/ToggleSwitch";
import { OPEN_DIALOGUE } from "../../store/dialogue/dialogue.type";
import { deleteLanguage, downloadTranslationsCSV, getAllLanguages, toggleLanguage } from "../../store/language/language.action";

const LanguageTable = () => {
    const dispatch = useDispatch();
    const { can } = usePermission();

    const canCreate = can("App Language", "Create");
    const canEdit = can("App Language", "Edit");
    const canDelete = can("App Language", "Delete");

    const { languages, totalLanguages } = useSelector((state) => state.language);

    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);
    const [search, setSearch] = useState("");
    const [data, setData] = useState([]);

    useEffect(() => {
        dispatch(getAllLanguages({ start: page, limit: size, search }));
    }, [dispatch, page, size, search]);

    useEffect(() => {
        setData(languages);
    }, [languages]);

    const handlePageChange = (event, pageNumber) => {
        setPage(pageNumber);
    };

    const handleRowsPerPage = (value) => {
        setSize(parseInt(value));
        setPage(1);
    };

    const handleToggleActive = (row) => {
        dispatch(toggleLanguage({ languageCode: row.languageCode, toggleType: 1 }));
    };

    const handleToggleDefault = (row) => {
        dispatch(toggleLanguage({ languageCode: row.languageCode, toggleType: 2 }));
    };

    const handleDelete = (row) => {
        warning().then((res) => {
            if (res) {
                dispatch(deleteLanguage(row.languageCode));
            }
        });
    };

    const handleDownloadCSV = () => {
        warning().then((res) => {
            if (res) {
                dispatch(downloadTranslationsCSV()).then((res) => {
                    if (res && res.data) {
                        const blob = new Blob([res.data], { type: "text/csv" });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement("a");
                        link.href = url;
                        link.setAttribute("download", "translations.csv");
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                    }
                }).catch((err) => {
                    console.log("Download Error: ", err);
                });
            }
        });
    };

    const handleFilterData = (filteredData) => {
        if (typeof filteredData === "string") {
            setSearch(filteredData);
            setPage(1);
        } else {
            setData(filteredData);
        }
    };

    const columns = [
        {
            Header: "No",
            width: "20px",
            Cell: ({ index }) => (
                <span className="text-white">{(page - 1) * size + index + 1}</span>
            ),
        },
        {
            Header: "Icon",
            body: "languageIcon",
            Cell: ({ row }) => (
                <img
                    src={row?.languageIcon.startsWith("http") ? row?.languageIcon : `${baseURL}${row?.languageIcon}` || "/images/defaultImageGlobal.png"}
                    alt="Icon"
                    style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "8px" }}
                    onError={(e) => { e.target.src = "/defaultImage.png" }}
                />
            ),
        },
        {
            Header: "Title",
            body: "languageTitle",
            Cell: ({ row }) => <span className="text-capitalize text-white">{row?.languageTitle}</span>,
        },
        {
            Header: "Code",
            body: "languageCode",
            Cell: ({ row }) => <span className="text-uppercase text-white">{row?.languageCode}</span>,
        },
        {
            Header: "Localized Title",
            body: "localLanguageTitle",
            Cell: ({ row }) => <span className="text-capitalize text-white">{row?.localLanguageTitle}</span>,
        },
        ...(canEdit ? [
            {
                Header: "Active",
                body: "isActive",
                Cell: ({ row }) => (
                    <ToggleSwitch
                        value={row?.isActive}
                        onClick={() => handleToggleActive(row)}
                    />
                ),
            }
        ] : []),
        ...(canEdit ? [
            {
                Header: "Default",
                body: "isDefault",
                Cell: ({ row }) => (
                    <ToggleSwitch
                        value={row?.isDefault}
                        onClick={() => handleToggleDefault(row)}
                    />
                ),
            }
        ] : []),
        {
            Header: "Errors",
            body: "errorCount",
            Cell: ({ row }) => <span style={{ color: row?.errorCount > 0 ? "#ff4c51" : "#4caf50", fontWeight: "bold" }}>{row?.errorCount || 0}</span>,
        },
        ...(canEdit ? [
            {
                Header: "Edit",
                body: "action",
                Cell: ({ row }) => (
                    <Iconb
                        newClass={`themeFont boxCenter infobtn userBtn fs-5`}
                        btnIcon={<CreateOutlinedIcon sx={{ color: '#737272' }} />}
                        style={{
                            borderRadius: "50px",
                            margin: "auto",
                            height: "45px",
                            width: "45px",
                            color: "#160d98",


                        }}
                        isImage={true}
                        onClick={() => dispatch({ type: OPEN_DIALOGUE, payload: { type: "language", data: row } })}

                    />

                ),
            }
        ] : []),
        ...(canDelete ? [
            {
                Header: "Delete",
                body: "action",
                Cell: ({ row }) => (
                    <Iconb
                        newClass={`themeFont boxCenter killbtn userBtn fs-5`}
                        btnIcon={<DeleteIcon sx={{ color: '#FF4C51' }} />}
                        style={{
                            borderRadius: "50px",
                            margin: "auto",
                            height: "45px",
                            width: "45px",
                            color: "#160d98",

                            padding: "0px"

                        }}
                        isImage={true}
                        isDeleted={true}
                        onClick={() => handleDelete(row)}

                    />
                ),
            }
        ] : []),
        {
            Header: "Info",
            body: "action",
            Cell: ({ row }) => (
                <Iconb
                    newClass={`themeFont boxCenter infobtn userBtn fs-5`}
                    btnIcon={<InfoOutlined sx={{ color: '#737272' }} />}
                    style={{
                        borderRadius: "50px",
                        margin: "auto",
                        height: "45px",
                        width: "45px",
                        color: "#160d98",

                        padding: "0px"
                    }}
                    isImage={true}
                    isDeleted={true}
                    onClick={() => dispatch({ type: OPEN_DIALOGUE, payload: { type: "translationInfo", data: row } })}

                />
            ),
        }
    ];

    return (
        <div className="mainUserTable">
            <div className="userTable">
                <div className="col-12 headname">App Language</div>
                <div className="userMain">
                    <div className="tableMain mt-2">
                        <div className="userHeader primeHeader">
                            <div className="row">
                                <div className="col-12 col-md-4 d-flex gap-2 align-items-center">
                                    {canCreate && (
                                        <Button
                                                                newClass={`whiteFont`}
                                                                btnColor={`btnBlackPrime `}
                                                                btnIcon={`fa-solid fa-plus`}
                                                                btnName={`Add`}
                                                                onClick={() => dispatch({ type: OPEN_DIALOGUE, payload: { type: "language" } })}
                                                                style={{ borderRadius: "5px", padding: "8px 32px", background: "#C41E3A" }}
                                                              />
                                    )}
                                    {canEdit && (
                                        <Button
                                            newClass={`whiteFont`}
                                            btnColor={`btnBlackPrime`}
                                            btnIcon={"fa-solid fa-arrow-up"}
                                            btnName={"Upload"}
                                            onClick={() => dispatch({ type: OPEN_DIALOGUE, payload: { type: "uploadCSV" } })}
                                            style={{ borderRadius: "5px", background: "#C41E3A", padding: "8px 32px" }}
                                        />
                                    )}
                                    <Button
                                        newClass={`whiteFont`}
                                        btnColor={`btnBlackPrime`}
                                        btnIcon={"fa-solid fa-arrow-down"}
                                        btnName={"Download"}
                                        onClick={handleDownloadCSV}
                                        style={{ borderRadius: "5px", background: "#C41E3A", padding: "8px 32px" }}
                                    />
                                </div>
                                <div className="col-12 col-md-8">
                                    <Searching
                                        type="server"
                                        data={languages}
                                        setData={setData}
                                        onFilterData={handleFilterData}
                                        serverSearching={handleFilterData}
                                        button={true}
                                        setSearchValue={setSearch}
                                        searchValue={search}
                                    />
                                </div>
                            </div>
                        </div>
                        <Table
                            data={data}
                            mapData={columns}
                            serverPerPage={size}
                            serverPage={page}
                            type={"server"}
                        />
                        <Pagination
                            type={"server"}
                            serverPage={page}
                            serverPerPage={size}
                            totalData={totalLanguages}
                            setCurrentPage={setPage}
                            onPageChange={handlePageChange}
                            onRowsPerPageChange={handleRowsPerPage}
                        />
                    </div>
                </div>
                <div className="userFooter primeFooter"></div>
            </div>
        </div>
    );
};

export default LanguageTable;
